/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";

import { offlineQueue } from "../utils/OfflineQueue";
import SocketClient from "../utils/SocketClient";

const OfflineStatusHandler = ({ updateConnectionStatus }) => {
  const callbackRef = useRef(updateConnectionStatus);
  const [lastReconnectAttempt, setLastReconnectAttempt] = useState(0);
  const isOnlineRef = useRef(navigator.onLine);
  const socketConnectionRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);

  const attemptReconnect = useRef(
    debounce(
      () => {
        const now = Date.now();

        if (now - lastReconnectAttempt < 5000) {
          // 5 seconds
          console.log(
            "[OfflineStatusHandler] Skipping reconnect attempt - too soon after previous attempt"
          );
          return;
        }

        setLastReconnectAttempt(now);

        if (SocketClient) {
          SocketClient.reconnectIfNeeded()
            .then((connected) => {
              socketConnectionRef.current = connected;
              console.log(
                `[OfflineStatusHandler] Reconnection attempt ${
                  connected ? "succeeded" : "failed"
                }`
              );

              if (connected) {
                setTimeout(() => {
                  try {
                    offlineQueue.processQueue();
                  } catch (err) {
                    console.error(
                      "[OfflineStatusHandler] Error processing offline queue:",
                      err
                    );
                  }
                }, 2000); // 2 seconds
              }
            })
            .catch((err) => {
              console.error(
                "[OfflineStatusHandler] Error during reconnection attempt:",
                err
              );
              socketConnectionRef.current = false;
            });
        }
      },
      1000,
      { leading: true, trailing: true }
    ) // 1 second
  ).current;

  const handleConnectionStatusChange = (connected) => {
    socketConnectionRef.current = connected;

    if (callbackRef.current) {
      callbackRef.current(connected);
    }
  };

  useEffect(() => {
    callbackRef.current = updateConnectionStatus;

    SocketClient.setConnectionStatusCallback(handleConnectionStatusChange);

    const handleOnline = () => {
      console.log("[OfflineStatusHandler] Browser online event fired!");
      isOnlineRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        attemptReconnect();
      }, 1000); // 1 second delay
    };

    const handleOffline = () => {
      console.log("[OfflineStatusHandler] Browser offline event fired!");
      isOnlineRef.current = false;
      handleConnectionStatusChange(false);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (
        !document.hidden &&
        navigator.onLine &&
        !socketConnectionRef.current
      ) {
        attemptReconnect();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    handleConnectionStatusChange(SocketClient.isConnected());

    return () => {
      SocketClient.removeConnectionStatusCallback(handleConnectionStatusChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [updateConnectionStatus, attemptReconnect]);

  return null;
};

export default OfflineStatusHandler;
